import { FC, useEffect, useState } from "react";
import {
    Box, Button, Typography, Modal, Backdrop, Fade, Alert, Stack, IconButton
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { getDeviceFingerprint } from "../../../utils/deviceFingerprint";
import { http } from "../../../utils/Http";

interface MfaEmailModalProps {
    open: boolean;
    userId: number;
    onSuccess: (token: string) => void;
    onClose: () => void;
}

const MfaEmailModal: FC<MfaEmailModalProps> = ({
    open,
    userId,
    onSuccess,
    onClose,
}) => {
    const [code, setCode] = useState<string[]>(Array(6).fill(""));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const [resendLoading, setResendLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();

        const pastedData = e?.clipboardData?.getData("text").trim();

        // Autorise uniquement 6 chiffres
        if (!/^\d{6}$/.test(pastedData)) {
            setError("Le code collé doit contenir 6 chiffres.");
            return;
        }

        const newCode = pastedData.split("");
        setCode(newCode);

        // Focus sur le dernier champ
        const lastInput = document.getElementById("mfa-5");
        lastInput?.focus();
    };

    /* ⏱ Cooldown resend */
    useEffect(() => {
        if (cooldown === 0) return;
        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    useEffect(() => {
        if (code.every(d => d !== "")) {
            handleVerify();
        }
    }, [code]);

    /* 🔢 Code MFA (6 inputs) */
    const handleCodeChange = (value: string, index: number) => {
        if (!/^\d?$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        const nextInput = document.getElementById(`mfa-${index + 1}`);
        if (value && nextInput) nextInput.focus();
    };

    /* ✅ Vérification */
    const handleVerify = async () => {
        const finalCode = code.join("");

        if (finalCode.length !== 6) {
            setError("Veuillez saisir le code à 6 chiffres.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const fingerprint = await getDeviceFingerprint();

            const response = await http.post(
                "user/login/mfa",
                {
                    user_id: userId,
                    code: finalCode,
                    device_fingerprint: fingerprint,
                },
                { withCredentials: true }
            );

            onSuccess(response?.data?.token);
            setCode(Array(6).fill(""));
        } catch (err: any) {
            console.log(err);

            setError(
                err?.response?.data?.message ||
                "Code invalide ou expiré."
            );
        } finally {
            setLoading(false);
        }
    };

    /* 🔁 Renvoyer le code */
    const handleResendCode = async () => {
        setResendLoading(true);
        setError("");
        setSuccessMessage("");

        try {
            await http.post(
                "user/login/mfa/resend",
                { user_id: userId },
                { withCredentials: true }
            );

            setSuccessMessage("Un nouveau code a été envoyé.");
            setCooldown(60);
        } catch {
            setError("Impossible de renvoyer le code.");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Modal open={open} closeAfterTransition BackdropComponent={Backdrop}>
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 420,
                        bgcolor: "background.paper",
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    {/* ❌ Close */}
                    <IconButton
                        onClick={onClose}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* 🔐 Header */}
                    <Stack alignItems="center" spacing={1} mb={3}>
                        <SecurityIcon color="warning" sx={{ fontSize: 40 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Vérification de sécurité
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Un code à usage unique a été envoyé sur votre e-mail
                        </Typography>
                    </Stack>

                    {/* ⚠️ Alerts */}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                    {/* 🔢 MFA Inputs */}
                    <Stack direction="row" spacing={1} justifyContent="center" mb={3}>
                        {code.map((digit, index) => (
                            <Box
                                key={index}
                                component="input"
                                id={`mfa-${index}`}
                                value={digit}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                name={`mfa-${index}`}
                                onChange={(e: any) => handleCodeChange(e.target.value, index)}
                                onPaste={handlePaste}
                                maxLength={1}
                                style={{
                                    width: 48,
                                    height: 56,
                                    fontSize: 24,
                                    textAlign: "center",
                                    borderRadius: 8,
                                    border: "1px solid #ccc",
                                }}
                            />
                        ))}
                    </Stack>

                    {/* ✅ Validate */}
                    <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        sx={{ textTransform: "none", py: 1.2 }}
                        onClick={handleVerify}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress color="warning" size={24} /> : "Vérifier"}
                    </Button>

                    {/* 🔁 Resend */}
                    <Button
                        fullWidth
                        variant="text"
                        color="warning"
                        sx={{ mt: 2, textTransform: "none" }}
                        startIcon={<RefreshIcon />}
                        disabled={resendLoading || cooldown > 0}
                        onClick={handleResendCode}
                    >
                        {cooldown > 0
                            ? `Renvoyer le code (${cooldown}s)`
                            : "Renvoyer le code"}
                    </Button>
                </Box>
            </Fade>
        </Modal>
    );
};

export default MfaEmailModal;
